import React, { useState, useEffect, useRef } from 'react';
import { Search, UserPlus, User, Check, Building, Phone, MapPin } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import apiService from '../../../services/apiService';
import Modal from '../../../components/ui/Modal';

const AsignarClienteModal = ({ clientes, catalogos, onClose, onClienteSeleccionado, onClienteCreado }) => {
  const { showToast, setLoading } = useApp();

  // Tabs: seleccionar existente o crear nuevo
  const [activeTab, setActiveTab] = useState('existente');

  // Estado para búsqueda de cliente existente
  const [busqueda, setBusqueda] = useState('');
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);

  // Estado para crear nuevo cliente
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '',
    telefono: '',
    direccion: '',
    // Datos fiscales
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal_fiscal: '',
    uso_cfdi: 'G03', // Gastos en general por defecto
    email_facturacion: ''
  });
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);

  const searchInputRef = useRef(null);

  // Enfocar el input de búsqueda al abrir
  useEffect(() => {
    if (activeTab === 'existente' && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [activeTab]);

  // Filtrar clientes cuando cambia la búsqueda
  useEffect(() => {
    if (!busqueda.trim()) {
      setClientesFiltrados(clientes.slice(0, 20)); // Mostrar los primeros 20
    } else {
      const termino = busqueda.toLowerCase();
      const filtrados = clientes.filter(c =>
        c.nombre?.toLowerCase().includes(termino) ||
        c.telefonos?.some(t => t.telefono?.includes(termino)) ||
        c.datos_fiscales?.some(df => df.rfc?.toLowerCase().includes(termino))
      ).slice(0, 20);
      setClientesFiltrados(filtrados);
    }
  }, [busqueda, clientes]);

  const handleInputChange = (field, value) => {
    setNuevoCliente(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!nuevoCliente.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    // Validar datos fiscales requeridos
    if (!nuevoCliente.rfc.trim()) {
      newErrors.rfc = 'RFC es requerido para facturar';
    } else {
      const rfcLength = nuevoCliente.rfc.trim().length;
      if (rfcLength !== 12 && rfcLength !== 13) {
        newErrors.rfc = 'RFC debe tener 12 (moral) o 13 (física) caracteres';
      }
    }

    if (!nuevoCliente.razon_social.trim()) {
      newErrors.razon_social = 'Razón social es requerida';
    }

    if (!nuevoCliente.regimen_fiscal) {
      newErrors.regimen_fiscal = 'Régimen fiscal es requerido';
    }

    if (!nuevoCliente.codigo_postal_fiscal.trim()) {
      newErrors.codigo_postal_fiscal = 'Código postal fiscal es requerido';
    } else if (!/^\d{5}$/.test(nuevoCliente.codigo_postal_fiscal.trim())) {
      newErrors.codigo_postal_fiscal = 'Código postal debe tener 5 dígitos';
    }

    if (nuevoCliente.email_facturacion && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoCliente.email_facturacion)) {
      newErrors.email_facturacion = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCrearCliente = async () => {
    if (!validateForm()) {
      return;
    }

    setGuardando(true);
    try {
      // Crear el cliente con datos fiscales
      const clienteData = {
        nombre: nuevoCliente.nombre.trim(),
        telefonos: nuevoCliente.telefono ? [{ telefono: nuevoCliente.telefono, tipo: 'principal', whatsapp: false }] : [],
        direcciones: nuevoCliente.direccion ? [{ direccion: nuevoCliente.direccion, es_principal: true }] : [],
        datos_fiscales: [{
          rfc: nuevoCliente.rfc.toUpperCase().trim(),
          razon_social: nuevoCliente.razon_social.trim(),
          regimen_fiscal: nuevoCliente.regimen_fiscal,
          codigo_postal_fiscal: nuevoCliente.codigo_postal_fiscal.trim(),
          uso_cfdi: nuevoCliente.uso_cfdi || 'G03',
          email_facturacion: nuevoCliente.email_facturacion.trim(),
          es_principal: true
        }],
        activo: true
      };

      const result = await apiService.crearCliente(clienteData);

      if (result.success) {
        showToast('Cliente creado exitosamente', 'success');

        // Cargar el cliente completo con sus datos fiscales
        const clienteCompleto = {
          id: result.data.id,
          nombre: nuevoCliente.nombre.trim(),
          datos_fiscales: [{
            id: result.data.datos_fiscal_id || null,
            cliente_id: result.data.id,
            rfc: nuevoCliente.rfc.toUpperCase().trim(),
            razon_social: nuevoCliente.razon_social.trim(),
            regimen_fiscal: nuevoCliente.regimen_fiscal,
            codigo_postal_fiscal: nuevoCliente.codigo_postal_fiscal.trim(),
            uso_cfdi: nuevoCliente.uso_cfdi || 'G03',
            email_facturacion: nuevoCliente.email_facturacion.trim(),
            es_principal: 1
          }]
        };

        onClienteCreado(clienteCompleto);
      } else {
        showToast(result.message || 'Error al crear cliente', 'error');
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
      showToast('Error al crear cliente', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const handleSeleccionarCliente = () => {
    if (!clienteSeleccionado) {
      showToast('Seleccione un cliente', 'warning');
      return;
    }
    onClienteSeleccionado(clienteSeleccionado);
  };

  // Determinar tipo de persona por longitud de RFC
  const getTipoPersona = () => {
    const rfcLength = nuevoCliente.rfc.trim().length;
    if (rfcLength === 12) return 'moral';
    if (rfcLength === 13) return 'fisica';
    return null;
  };

  // Filtrar regímenes según tipo de persona
  const getRegimenesFiltrados = () => {
    const tipoPersona = getTipoPersona();
    if (!tipoPersona || !catalogos.regimenesFiscales) return catalogos.regimenesFiscales || [];

    return catalogos.regimenesFiscales.filter(r =>
      tipoPersona === 'fisica' ? r.persona_fisica : r.persona_moral
    );
  };

  // Filtrar usos CFDI según tipo de persona
  const getUsosCFDIFiltrados = () => {
    const tipoPersona = getTipoPersona();
    if (!tipoPersona || !catalogos.usosCFDI) return catalogos.usosCFDI || [];

    return catalogos.usosCFDI.filter(u =>
      tipoPersona === 'fisica' ? u.persona_fisica : u.persona_moral
    );
  };

  return (
    <Modal
      title="Asignar Cliente para Facturar"
      onClose={onClose}
      size="large"
    >
      <div className="asignar-cliente-modal">
        {/* Tabs */}
        <div className="asignar-cliente-tabs">
          <button
            className={`tab-btn ${activeTab === 'existente' ? 'active' : ''}`}
            onClick={() => setActiveTab('existente')}
          >
            <User size={16} />
            Cliente Existente
          </button>
          <button
            className={`tab-btn ${activeTab === 'nuevo' ? 'active' : ''}`}
            onClick={() => setActiveTab('nuevo')}
          >
            <UserPlus size={16} />
            Crear Nuevo Cliente
          </button>
        </div>

        {/* Tab: Cliente Existente */}
        {activeTab === 'existente' && (
          <div className="tab-content">
            <div className="search-box">
              <Search size={18} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar por nombre, teléfono o RFC..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            <div className="clientes-lista">
              {clientesFiltrados.length === 0 ? (
                <div className="empty-state">
                  <User size={32} />
                  <p>No se encontraron clientes</p>
                </div>
              ) : (
                clientesFiltrados.map(cliente => {
                  const isSelected = clienteSeleccionado?.id === cliente.id;
                  const tieneDatosFiscales = cliente.datos_fiscales?.some(df => df.rfc && df.razon_social);

                  return (
                    <div
                      key={cliente.id}
                      className={`cliente-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setClienteSeleccionado(cliente)}
                    >
                      <div className="cliente-check">
                        {isSelected && <Check size={16} />}
                      </div>
                      <div className="cliente-info">
                        <div className="cliente-nombre">{cliente.nombre}</div>
                        {cliente.telefonos?.[0]?.telefono && (
                          <div className="cliente-detalle">
                            <Phone size={12} />
                            {cliente.telefonos[0].telefono}
                          </div>
                        )}
                        {cliente.datos_fiscales?.[0]?.rfc && (
                          <div className="cliente-detalle">
                            <Building size={12} />
                            RFC: {cliente.datos_fiscales[0].rfc}
                          </div>
                        )}
                      </div>
                      <div className="cliente-status">
                        {tieneDatosFiscales ? (
                          <span className="badge-fiscal ok">Datos Fiscales</span>
                        ) : (
                          <span className="badge-fiscal warning">Sin Datos Fiscales</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {clienteSeleccionado && !clienteSeleccionado.datos_fiscales?.some(df => df.rfc && df.razon_social) && (
              <div className="warning-box">
                <Building size={16} />
                <span>Este cliente no tiene datos fiscales. Podrá agregarlos en el siguiente paso.</span>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-cancelar" onClick={onClose}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleSeleccionarCliente}
                disabled={!clienteSeleccionado}
              >
                <Check size={16} />
                Seleccionar Cliente
              </button>
            </div>
          </div>
        )}

        {/* Tab: Crear Nuevo Cliente */}
        {activeTab === 'nuevo' && (
          <div className="tab-content">
            <div className="form-section">
              <h4>
                <User size={16} />
                Datos del Cliente
              </h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre / Razón Social Comercial <span className="required">*</span></label>
                  <input
                    type="text"
                    value={nuevoCliente.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Nombre del cliente"
                    className={errors.nombre ? 'error' : ''}
                  />
                  {errors.nombre && <span className="error-message">{errors.nombre}</span>}
                </div>
              </div>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>
                    <Phone size={14} /> Teléfono
                  </label>
                  <input
                    type="text"
                    value={nuevoCliente.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value.replace(/\D/g, ''))}
                    placeholder="10 dígitos"
                    maxLength={10}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <MapPin size={14} /> Dirección
                  </label>
                  <input
                    type="text"
                    value={nuevoCliente.direccion}
                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                    placeholder="Dirección del cliente"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>
                <Building size={16} />
                Datos Fiscales para Facturación
              </h4>
              <div className="form-row two-cols">
                <div className="form-group">
                  <label>RFC <span className="required">*</span></label>
                  <input
                    type="text"
                    value={nuevoCliente.rfc}
                    onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                    placeholder="XAXX010101000"
                    maxLength={13}
                    className={errors.rfc ? 'error' : ''}
                  />
                  {errors.rfc && <span className="error-message">{errors.rfc}</span>}
                </div>
                <div className="form-group">
                  <label>Código Postal Fiscal <span className="required">*</span></label>
                  <input
                    type="text"
                    value={nuevoCliente.codigo_postal_fiscal}
                    onChange={(e) => handleInputChange('codigo_postal_fiscal', e.target.value.replace(/\D/g, ''))}
                    placeholder="12345"
                    maxLength={5}
                    className={errors.codigo_postal_fiscal ? 'error' : ''}
                  />
                  {errors.codigo_postal_fiscal && <span className="error-message">{errors.codigo_postal_fiscal}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Razón Social (como aparecerá en factura) <span className="required">*</span></label>
                <input
                  type="text"
                  value={nuevoCliente.razon_social}
                  onChange={(e) => handleInputChange('razon_social', e.target.value)}
                  placeholder="Nombre o Razón Social para facturación"
                  className={errors.razon_social ? 'error' : ''}
                />
                {errors.razon_social && <span className="error-message">{errors.razon_social}</span>}
              </div>

              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Régimen Fiscal <span className="required">*</span></label>
                  <select
                    value={nuevoCliente.regimen_fiscal}
                    onChange={(e) => handleInputChange('regimen_fiscal', e.target.value)}
                    className={errors.regimen_fiscal ? 'error' : ''}
                  >
                    <option value="">Seleccionar...</option>
                    {getRegimenesFiltrados().map(r => (
                      <option key={r.clave} value={r.clave}>
                        {r.clave} - {r.descripcion}
                      </option>
                    ))}
                  </select>
                  {errors.regimen_fiscal && <span className="error-message">{errors.regimen_fiscal}</span>}
                </div>
                <div className="form-group">
                  <label>Uso CFDI</label>
                  <select
                    value={nuevoCliente.uso_cfdi}
                    onChange={(e) => handleInputChange('uso_cfdi', e.target.value)}
                  >
                    <option value="">Seleccionar...</option>
                    {getUsosCFDIFiltrados().map(u => (
                      <option key={u.clave} value={u.clave}>
                        {u.clave} - {u.descripcion}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Email para envío de factura</label>
                <input
                  type="email"
                  value={nuevoCliente.email_facturacion}
                  onChange={(e) => handleInputChange('email_facturacion', e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className={errors.email_facturacion ? 'error' : ''}
                />
                {errors.email_facturacion && <span className="error-message">{errors.email_facturacion}</span>}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancelar" onClick={onClose} disabled={guardando}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleCrearCliente}
                disabled={guardando}
              >
                {guardando ? 'Guardando...' : (
                  <>
                    <UserPlus size={16} />
                    Crear Cliente y Continuar
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .asignar-cliente-modal {
          padding: 0;
        }

        .asignar-cliente-tabs {
          display: flex;
          gap: 0;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 20px;
        }

        .asignar-cliente-tabs .tab-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: transparent;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s;
        }

        .asignar-cliente-tabs .tab-btn:hover {
          color: #4f46e5;
          background: #f5f3ff;
        }

        .asignar-cliente-tabs .tab-btn.active {
          color: #4f46e5;
          border-bottom-color: #4f46e5;
        }

        .tab-content {
          padding: 0 4px;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .search-box input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 14px;
          outline: none;
        }

        .search-box svg {
          color: #9ca3af;
        }

        .clientes-lista {
          max-height: 300px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .cliente-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background 0.15s;
        }

        .cliente-item:hover {
          background: #f9fafb;
        }

        .cliente-item.selected {
          background: #eef2ff;
          border-color: #c7d2fe;
        }

        .cliente-item:last-child {
          border-bottom: none;
        }

        .cliente-check {
          width: 24px;
          height: 24px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cliente-item.selected .cliente-check {
          background: #4f46e5;
          border-color: #4f46e5;
          color: white;
        }

        .cliente-info {
          flex: 1;
          min-width: 0;
        }

        .cliente-nombre {
          font-weight: 500;
          color: #111827;
          margin-bottom: 4px;
        }

        .cliente-detalle {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #6b7280;
        }

        .cliente-status {
          flex-shrink: 0;
        }

        .badge-fiscal {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        .badge-fiscal.ok {
          background: #d1fae5;
          color: #065f46;
        }

        .badge-fiscal.warning {
          background: #fef3c7;
          color: #92400e;
        }

        .empty-state {
          padding: 40px 20px;
          text-align: center;
          color: #9ca3af;
        }

        .empty-state svg {
          margin-bottom: 8px;
        }

        .warning-box {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 13px;
          color: #92400e;
        }

        .form-section {
          margin-bottom: 20px;
        }

        .form-section h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-row {
          margin-bottom: 12px;
        }

        .form-row.two-cols {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          margin-bottom: 12px;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .form-group label .required {
          color: #ef4444;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #ef4444;
        }

        .error-message {
          display: block;
          font-size: 12px;
          color: #ef4444;
          margin-top: 4px;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancelar {
          padding: 10px 20px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-cancelar:hover {
          background: #e5e7eb;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #4f46e5;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          color: white;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #4338ca;
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
      `}</style>
    </Modal>
  );
};

export default AsignarClienteModal;
